import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Clock, Camera, Users, Radio, ChevronLeft, ChevronRight, ImageOff, Route } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (path) => `${BACKEND}/api/visits/photo/${path}?auth=${localStorage.getItem("sc_token") || ""}`;
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDur = (m) => (m == null ? "—" : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const shiftDay = (s, n) => { const d = new Date(`${s}T00:00:00`); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const PALETTE = ["#e05b4f", "#2563eb", "#059669", "#d97706", "#7c3aed", "#db2777", "#0891b2", "#65a30d", "#ea580c", "#4f46e5"];
const INDIA = [22.5, 79.0];

function FitBounds({ points }) {
  const map = useMap();
  useMemo(() => {
    if (points.length === 1) map.setView(points[0], 13);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 14 });
    else map.setView(INDIA, 5);
  }, [map, points]);
  return null;
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={testid}>
      <Icon className="h-4 w-4 text-brand" />
      <span className="text-sm font-bold text-slate-900 font-mono">{value}</span>
      <span className="text-xs text-slate-500">{label}</span>
    </div>
  );
}

function EventCard({ ev, color, selected, onSelect, onPhoto, order }) {
  return (
    <button type="button" onClick={onSelect}
      className={`w-full text-left rounded-lg border p-3 transition-colors ${selected ? "border-brand bg-brand/5" : "border-slate-200 bg-white hover:bg-slate-50"}`}
      data-testid={`checkin-event-${ev.id}`}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 h-5 w-5 rounded-full shrink-0 text-[10px] font-bold text-white flex items-center justify-center" style={{ background: color }} data-testid={`checkin-order-${ev.id}`}>{order || "·"}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800 truncate">{ev.user_name}</p>
          <p className="text-xs text-slate-600 truncate">{ev.entity_name || "—"} <span className="text-slate-400">· {ev.entity_type}</span></p>
          <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(ev.check_in_at)} → {ev.live ? <span className="text-emerald-600 font-semibold">live</span> : fmtTime(ev.check_out_at)}</span>
            <span className="font-mono font-semibold text-slate-700" data-testid={`checkin-duration-${ev.id}`}>{fmtDur(ev.duration_minutes)}</span>
            {ev.latitude == null && <span className="inline-flex items-center gap-1 text-amber-600"><MapPin className="h-3 w-3" /> no location</span>}
          </p>
          {ev.photos.length > 0 && (
            <div className="flex gap-1.5 mt-2">
              {ev.photos.slice(0, 4).map((p) => (
                <img key={p.id} src={photoUrl(p.path)} alt="" className="h-12 w-12 rounded object-cover border border-slate-200 cursor-zoom-in"
                  onClick={(e) => { e.stopPropagation(); onPhoto(p.path); }} data-testid={`checkin-photo-${p.id}`} />
              ))}
              {ev.photos.length > 4 && <span className="h-12 w-12 rounded border border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center justify-center">+{ev.photos.length - 4}</span>}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

export default function CheckinMap() {
  const [date, setDate] = useState(todayStr());
  const [person, setPerson] = useState("all");
  const [selected, setSelected] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [routes, setRoutes] = useState(true);
  const { data, isLoading } = useQuery({
    queryKey: ["visits-map", date, person],
    queryFn: async () => (await api.get("/visits/map", { params: { date, user_id: person === "all" ? "" : person } })).data,
    refetchInterval: 60000,
  });
  const events = data?.events || [];
  const colors = useMemo(() => {
    const ids = [...new Set(events.map((e) => e.user_id))];
    return Object.fromEntries(ids.map((id, i) => [id, PALETTE[i % PALETTE.length]]));
  }, [events]);
  const located = events.filter((e) => e.latitude != null && e.longitude != null);
  const points = useMemo(() => located.map((e) => [e.latitude, e.longitude]), [located]);
  const order = useMemo(() => {
    const byUser = {};
    [...located].sort((a, b) => (a.check_in_at || "").localeCompare(b.check_in_at || "")).forEach((e) => {
      byUser[e.user_id] = byUser[e.user_id] || [];
      byUser[e.user_id].push(e);
    });
    const idx = {};
    Object.values(byUser).forEach((list) => list.forEach((e, i) => { idx[e.id] = i + 1; }));
    return { byUser, idx };
  }, [located]);
  const s = data?.summary || {};

  return (
    <div data-testid="checkin-map-page">
      <PageHeader title="Check-in Map" subtitle="Where the team checked in today — live status, time on site and photos.">
        <div className="inline-flex items-center rounded-md border border-slate-200 bg-white h-9">
          <button className="px-2 h-full text-slate-500 hover:text-slate-900" onClick={() => setDate(shiftDay(date, -1))} data-testid="checkin-map-prev"><ChevronLeft className="h-4 w-4" /></button>
          <input type="date" value={date} max={todayStr()} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-full text-sm px-1 bg-transparent outline-none" data-testid="checkin-map-date" />
          <button className="px-2 h-full text-slate-500 hover:text-slate-900 disabled:opacity-30" disabled={date >= todayStr()} onClick={() => setDate(shiftDay(date, 1))} data-testid="checkin-map-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <Select value={person} onValueChange={setPerson}>
          <SelectTrigger className="h-9 w-[180px]" data-testid="checkin-map-person"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Everyone</SelectItem>
            {(data?.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-9" onClick={() => setDate(todayStr())} data-testid="checkin-map-today">Today</Button>
        <Button variant={routes ? "default" : "outline"} size="sm" className={`h-9 gap-1.5 ${routes ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}`} onClick={() => setRoutes((v) => !v)} data-testid="checkin-map-routes-toggle">
          <Route className="h-4 w-4" /> Routes
        </Button>
      </PageHeader>

      <p className="text-[11px] text-slate-500 mb-3" data-testid="checkin-map-legend">Numbers show each salesperson's visit order for the day; dashed lines connect their sites in travel sequence.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <Stat icon={MapPin} label="check-ins" value={s.total ?? "—"} testid="checkin-stat-total" />
        <Stat icon={Users} label="people" value={s.people ?? "—"} testid="checkin-stat-people" />
        <Stat icon={Radio} label="live now" value={s.live ?? "—"} testid="checkin-stat-live" />
        <Stat icon={Clock} label="on site" value={fmtDur(s.minutes ?? 0)} testid="checkin-stat-minutes" />
        <Stat icon={Camera} label="photos" value={s.photos ?? "—"} testid="checkin-stat-photos" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 overflow-hidden" data-testid="checkin-map">
          <div className="h-[560px]">
            <MapContainer center={INDIA} zoom={5} className="h-full w-full" scrollWheelZoom>
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitBounds points={points} />
              {routes && Object.entries(order.byUser).filter(([, list]) => list.length > 1).map(([uid, list]) => (
                <Polyline key={`route-${uid}`} positions={list.map((e) => [e.latitude, e.longitude])}
                  pathOptions={{ color: colors[uid], weight: 3, opacity: 0.85, dashArray: "6 6" }} />
              ))}
              {located.map((e) => (
                <CircleMarker key={e.id} center={[e.latitude, e.longitude]} radius={selected === e.id ? 12 : 9}
                  pathOptions={{ color: "#fff", weight: 2, fillColor: colors[e.user_id], fillOpacity: e.live ? 1 : 0.8 }}
                  eventHandlers={{ click: () => setSelected(e.id) }}>
                  {routes && <Tooltip permanent direction="center" className="route-order-label">{order.idx[e.id]}</Tooltip>}
                  <Popup>
                    <div className="text-xs min-w-[180px]">
                      <p className="font-semibold text-slate-800">{e.user_name}</p>
                      <p className="text-slate-600">{e.entity_name} · {e.entity_type}</p>
                      <p className="text-slate-500 mt-1">{fmtTime(e.check_in_at)} → {e.live ? "live" : fmtTime(e.check_out_at)} · <b>{fmtDur(e.duration_minutes)}</b></p>
                      {e.address && <p className="text-slate-400 mt-1">{e.address}</p>}
                      {e.photos.length > 0 && (
                        <div className="flex gap-1 mt-2">{e.photos.slice(0, 3).map((p) => <img key={p.id} src={photoUrl(p.path)} alt="" className="h-10 w-10 rounded object-cover cursor-zoom-in" onClick={() => setPhoto(p.path)} />)}</div>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
          {located.length < events.length && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border-t border-amber-100 px-3 py-1.5" data-testid="checkin-map-unlocated">
              {events.length - located.length} check-in{events.length - located.length > 1 ? "s" : ""} without GPS location — listed on the right only.
            </p>
          )}
        </Card>

        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1" data-testid="checkin-event-list">
          {isLoading && <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>}
          {!isLoading && events.length === 0 && (
            <Card className="p-8 text-center text-sm text-slate-400" data-testid="checkin-map-empty">
              <ImageOff className="h-6 w-6 mx-auto mb-2 text-slate-300" /> No check-ins on this day.
            </Card>
          )}
          {events.map((e) => (
            <EventCard key={e.id} ev={e} color={colors[e.user_id]} order={order.idx[e.id]} selected={selected === e.id} onSelect={() => setSelected(e.id)} onPhoto={setPhoto} />
          ))}
        </div>
      </div>

      <Dialog open={!!photo} onOpenChange={(o) => !o && setPhoto(null)}>
        <DialogContent className="max-w-3xl p-2" data-testid="checkin-photo-dialog">
          {photo && <img src={photoUrl(photo)} alt="Site photo" className="w-full max-h-[80vh] object-contain rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
